import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, Chip,
} from '@mui/material';
import { Download as DownloadIcon, ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import axios from '../../api/axios';

const fmtCurrency = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MaterialsListDialog = ({ open, onClose, projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetch = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/projects/${projectId}/materials-list`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load materials list.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (open) fetch(); }, [open, fetch]);

  const downloadCsv = async () => {
    try {
      const response = await axios.get(
        `/api/projects/${projectId}/materials-list`,
        { params: { format: 'csv' }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const safeName = (data?.project?.name || 'project').replace(/[^a-z0-9_-]+/gi, '-').toLowerCase();
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}-materials.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download CSV.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShoppingCartIcon /> Materials shopping list
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && data && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Deduplicated by item title (case-insensitive). Same item across multiple rooms is summed
              into one line so you can take this to the supplier as a single shopping list.
            </Typography>
            {data.lines.length === 0 ? (
              <Alert severity="info">No materials items in this project yet.</Alert>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Rooms</TableCell>
                      <TableCell align="right">Lines</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.lines.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{l.title}</Typography>
                            <Chip
                              label={l.type === 'both' ? 'materials + labor' : l.type}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {l.rooms.join(' · ')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{l.line_count}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {fmtCurrency(l.total_cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Typography variant="h6">
                    Grand Total: {fmtCurrency(data.grand_total)}
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={downloadCsv}
          disabled={!data || data.lines.length === 0}
        >
          Download CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaterialsListDialog;
